import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Heart, Send, Loader2, Copy, Check, Info } from "lucide-react";

/**
 * Top-liked photos widget for the host dashboard.
 *
 * Shows the top 10 most-liked uploads on the event, with a "Send
 * thank-you blast" button that emails uploaders whose names match a
 * guest list / RSVP email.
 */
const TopLikedPhotosWidget = ({ event }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const [customMsg, setCustomMsg] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/uploads/top?limit=10`);
      setData(r.data);
    } catch (e) {
      console.debug("[top-liked] load skipped:", e?.message);
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const sendBlast = async () => {
    if (!window.confirm(`Send a thank-you email to the uploaders of your top ${data?.top?.length || 0} photos?`)) return;
    setBusy(true); setBlastResult(null);
    try {
      const r = await api.post(`/events/${event.id}/uploads/thank-you-blast`, {
        limit: 10,
        custom_message: customMsg.trim() || null,
      });
      setBlastResult(r.data);
    } catch (e) {
      setBlastResult({ error: e?.response?.data?.detail || "Blast failed." });
    } finally { setBusy(false); }
  };

  const copyPrintList = async () => {
    const lines = (data?.top || []).map((u, i) =>
      `${i + 1}. ${u.uploader_name || "Anonymous"} — ${u.like_count} like${u.like_count === 1 ? "" : "s"} — ${u.file_url || ""}`
    );
    await navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  if (!data) {
    return (
      <div data-testid="top-liked-widget-loading" className="bg-white rounded-3xl soft-shadow p-6">
        <Loader2 className="w-5 h-5 animate-spin text-coral-300" />
      </div>
    );
  }

  if (!data.top?.length) {
    return (
      <div data-testid="top-liked-widget-empty" className="bg-white rounded-3xl soft-shadow p-6">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-coral-300" />
          <h3 className="font-serif italic text-navy-700 text-2xl">Top-liked photos</h3>
        </div>
        <p className="text-sm text-navy-700/60 mt-2">
          No likes yet. Once your guests start hearting photos, your most-loved shots will show up here.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="top-liked-widget" className="bg-white rounded-3xl soft-shadow p-6">
      <div className="flex items-center gap-2 flex-wrap">
        <Heart className="w-5 h-5 text-coral-300" />
        <h3 className="font-serif italic text-navy-700 text-2xl">Top-liked photos</h3>
        <span className="text-xs text-navy-700/50">
          {data.total_liked} photo{data.total_liked === 1 ? "" : "s"} loved by guests
        </span>
      </div>

      <div data-testid="top-liked-grid" className="mt-5 grid grid-cols-5 gap-2">
        {data.top.map((u, i) => (
          <a
            key={u.id}
            href={u.file_url || "#"}
            target="_blank" rel="noreferrer"
            data-testid={`top-liked-tile-${u.id}`}
            className="relative aspect-square bg-cream-200 rounded-lg overflow-hidden group hover:opacity-95"
          >
            {u.kind === "video" ? (
              <video src={u.file_url} className="w-full h-full object-cover" muted />
            ) : (
              <img src={u.file_url} alt="" loading="lazy" className="w-full h-full object-cover" />
            )}
            <div className="absolute top-1 left-1 bg-white/90 rounded-full text-[10px] font-bold px-1.5 py-0.5 text-navy-700">
              #{i + 1}
            </div>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-[11px] text-white flex items-center gap-1">
              <Heart className="w-3 h-3 fill-coral-300 text-coral-300" /> {u.like_count}
            </div>
          </a>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={copyPrintList} data-testid="top-liked-copy"
          className="inline-flex items-center gap-1.5 bg-white border border-navy-700/10 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied print list" : "Copy print list"}
        </button>
        <button onClick={() => setCustomOpen((v) => !v)} data-testid="top-liked-blast-open"
          className="inline-flex items-center gap-1.5 bg-coral-300 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-coral-400">
          <Send className="w-3.5 h-3.5" /> Send thank-you
        </button>
      </div>

      {customOpen && (
        <div className="mt-4 rounded-2xl border border-cream-200 bg-cream-100 p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Custom note (optional)</div>
          <textarea
            value={customMsg}
            onChange={(e) => setCustomMsg(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Add a personal note — will appear inside the email."
            data-testid="top-liked-blast-msg"
            className="w-full px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white"
          />
          <div className="flex items-center gap-2">
            <button onClick={sendBlast} disabled={busy}
              data-testid="top-liked-blast-send"
              className="inline-flex items-center gap-1.5 bg-navy-700 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-navy-800 disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send now
            </button>
            <p className="text-[11px] text-navy-700/50 flex-1 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              We match uploader names to your guest list / RSVP emails. Uploaders without an email are listed after sending so you can reach them another way.
            </p>
          </div>
        </div>
      )}

      {blastResult && (
        <div data-testid="top-liked-blast-result"
          className={`mt-4 rounded-xl px-4 py-3 text-sm ${blastResult.error ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-800"}`}>
          {blastResult.error ? (
            blastResult.error
          ) : (
            <>
              Sent <strong>{blastResult.sent}</strong> thank-you email{blastResult.sent === 1 ? "" : "s"}.
              {blastResult.skipped_no_email > 0 && (
                <> {blastResult.skipped_no_email} uploader{blastResult.skipped_no_email === 1 ? "" : "s"} had no matching email.</>
              )}
              {blastResult.unmatched_uploaders?.length > 0 && (
                <div className="mt-2 text-xs">
                  Unmatched: {blastResult.unmatched_uploaders.slice(0, 6).join(", ")}
                  {blastResult.unmatched_uploaders.length > 6 && " …"}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TopLikedPhotosWidget;
